import Checkbox from '@repo/ui/checkbox'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getPeriods } from '../../../api/periods/get-periods'
import { getTrips } from '../../../api/trips/get-trips'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import {
  StaffPaymentStatusEnum,
  staffPaymentStatusEnumMapping
} from '../../../lib/types/enums/affected-staff/staff-payment-status.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { TravelExpensesSalaryStaffsFilters } from '../../../lib/types/models/travel-expenses/travel-expenses-staff'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

interface Props {
  filter?: TravelExpensesSalaryStaffsFilters
  onFilterChange: (filter: TravelExpensesSalaryStaffsFilters) => void
}

const TravelExpensesSalaryStaffFilter = ({ filter, onFilterChange }: Props) => {
  const { t } = useTranslate()
  function setFilter<K extends keyof TravelExpensesSalaryStaffsFilters>(
    key: K,
    value: TravelExpensesSalaryStaffsFilters[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips({ version: 'BASIC' })
  })

  return (
    <div className={'flex flex-col gap-3 mt-2'}>
      <MultiSelectInput
        label={'Pays'}
        variant="primary"
        searchable
        options={countries?.data.map(({ name, code }) => ({
          label: name,
          value: code
        }))}
        selectedOptions={filter?.countries?.map((code) => ({
          label: countries?.data.find((c) => c.code === code)?.name ?? '',
          value: code
        }))}
        onChange={(countries) => setFilter('countries', countries)}
      />

      <MultiSelectInput
        searchable
        label={'Périodes'}
        variant={'warning'}
        options={periods?.data?.map((period) => ({ label: period.name, value: period.id }))}
        onChange={(periods) => setFilter('periods', periods)}
        selectedOptions={filter?.periods?.map((periodId) => ({
          label: periods?.data?.find((p) => p.id === periodId)?.name || '',
          value: periodId
        }))}
      />

      <MultiSelectInput
        label={'Formules'}
        variant={'primary'}
        searchable
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula),
          value: formula.id
        }))}
        onChange={(formulas) => setFilter('formulas', formulas)}
        selectedOptions={filter?.formulas?.map((formulaId) => ({
          label: getTranslatedName(formulas?.data?.find((f) => f.id === formulaId)),
          value: formulaId
        }))}
      />

      <MultiSelectInput
        label={'Qualifications'}
        variant={'primary'}
        searchable
        options={Object.values(StaffQualificationEnum).map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
        onChange={(qualifications) => setFilter('qualifications', qualifications)}
        selectedOptions={filter?.qualifications?.map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
      />

      <MultiSelectInput
        label={'Poste'}
        variant={'primary'}
        searchable
        options={Object.values(StaffPositionEnum)
          .filter((position) => position !== StaffPositionEnum.AAT)
          .map((position) => ({
            label: t(staffPositionEnumMapping[position]),
            value: position
          }))}
        onChange={(positions) => setFilter('positions', positions)}
        selectedOptions={filter?.positions?.map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
      />

      <div className="flex space-x-4 justify-between">
        <DateStringInput
          label="Départ après le"
          value={filter?.departureDateFrom}
          mode="over"
          onChange={(date) => onFilterChange({ ...filter, departureDateFrom: date })}
        />
        <DateStringInput
          label="Départ avant le"
          value={filter?.departureDateTo}
          mode="over"
          onChange={(date) => onFilterChange({ ...filter, departureDateTo: date })}
        />
      </div>

      <div className="flex space-x-4 justify-between">
        <DateStringInput
          label="Retour après le"
          value={filter?.arrivalDateFrom}
          mode="over"
          onChange={(date) => onFilterChange({ ...filter, arrivalDateFrom: date })}
        />
        <DateStringInput
          label="Retour avant le"
          value={filter?.arrivalDateTo}
          mode="over"
          onChange={(date) => onFilterChange({ ...filter, arrivalDateTo: date })}
        />
      </div>

      <MultiSelectInput
        label={"Type d'hébergement"}
        variant={'primary'}
        searchable
        options={Object.values(AccomodationEnum).map((option) => ({
          label: accomodationEnumMapping[option],
          value: option
        }))}
        selectedOptions={filter?.accomodations?.map((accommodation) => ({
          label: accomodationEnumMapping[accommodation],
          value: accommodation
        }))}
        onChange={(accomodations) => setFilter('accomodations', accomodations)}
      />

      <MultiSelectInput
        label={'Voyages'}
        variant={'primary'}
        searchable
        options={trips?.data?.map((trip) => ({
          label: TripProperties.getTripLabel(trip),
          value: trip.id
        }))}
        selectedOptions={filter?.trips?.map((tripId) => ({
          label: TripProperties.getTripLabel(trips?.data?.find((t) => t.id === tripId)),
          value: tripId
        }))}
        onChange={(trips) => setFilter('trips', trips)}
      />

      <div className="flex space-x-4 justify-between">
        <DateStringInput
          label="Reçue après le"
          value={filter?.travelExpensesSentAtFrom}
          mode="over"
          onChange={(date) => onFilterChange({ ...filter, travelExpensesSentAtFrom: date })}
        />
        <DateStringInput
          label="Reçue avant le"
          value={filter?.travelExpensesSentAtTo}
          mode="over"
          onChange={(date) => onFilterChange({ ...filter, travelExpensesSentAtTo: date })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Typography variant="label-medium">Statut du virement</Typography>

        <div className="flex flex-row gap-2">
          {Object.values(StaffPaymentStatusEnum).map((paymentStatus) => (
            <Checkbox
              key={paymentStatus}
              labelVariant="small"
              labelClassName="text-grey-lighter"
              checked={filter?.paymentStatuses?.includes(paymentStatus)}
              label={staffPaymentStatusEnumMapping[paymentStatus].state}
              onChange={(checked) => {
                setFilter(
                  'paymentStatuses',
                  checked
                    ? [...(filter?.paymentStatuses || []), paymentStatus]
                    : filter?.paymentStatuses?.filter((t) => t !== paymentStatus)
                )
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default TravelExpensesSalaryStaffFilter
